'use client';

import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import { Skeleton } from '@savvy-kit/components/skeleton';
import Banner from '@savvy-kit/patterns/banner';
import { isNil } from 'es-toolkit';
import { StagedClientContactFields } from 'src/features/advisor-book-onboarding/components/cockpit/staged-client-contact-fields';
import {
  FIX_CONTACT_HOUSEHOLD_NOT_FOUND_DESCRIPTION,
  FIX_CONTACT_MISSING_PRIMARY_DESCRIPTION,
  FIX_CONTACT_MISSING_PRIMARY_TITLE,
  FIX_CONTACT_OPEN_BULK_UPLOAD_LABEL,
} from 'src/features/advisor-book-onboarding/constants/fix-contact-info-copy';
import { useFixContactInfo } from 'src/features/advisor-book-onboarding/hooks/use-fix-contact-info';

type FixContactInfoDialogProps = {
  readonly open: boolean;
  readonly stagedHouseholdUuid: string | null;
  readonly householdName: string | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onResolved: () => void;
  readonly onOpenBulkUpload: () => void;
};

export function FixContactInfoDialog({
  open,
  stagedHouseholdUuid,
  householdName,
  onOpenChange,
  onResolved,
  onOpenBulkUpload,
}: FixContactInfoDialogProps) {
  const {
    household,
    primaryClient,
    coClients,
    contactErrorCount,
    canResolve,
    isResolving,
    isLoading,
    loadError,
    mutations,
    resolve,
    refetch,
  } = useFixContactInfo({
    stagedHouseholdUuid,
    open,
    onResolved: () => {
      onResolved();
      onOpenChange(false);
    },
  });

  const titleHousehold = householdName ?? household?.displayName ?? 'household';
  const isSaving = !isNil(mutations.savingClientId);
  const resolveDisabled = !canResolve || isSaving;
  const resolveStatusId = 'fix-contact-resolve-status';
  const missingPrimaryClient = !isLoading && isNil(loadError) && !isNil(household) && isNil(primaryClient);

  function handleOpenBulkUpload() {
    onOpenChange(false);
    onOpenBulkUpload();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fix contact info</DialogTitle>
          <DialogDescription>
            {missingPrimaryClient
              ? `This household (${titleHousehold}) has no primary client to update.`
              : `Update missing or invalid contact details for ${titleHousehold}, then resolve to clear the block.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 overflow-y-auto">
          {isLoading && (
            <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading contact info">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {!isLoading && !isNil(loadError) && (
            <Banner
              variant="destructive"
              title="Couldn't load contact info"
              description="Something went wrong loading this household. Try again."
              actionButton={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void refetch();
                  }}
                >
                  Retry
                </Button>
              }
            />
          )}

          {!isLoading && isNil(loadError) && isNil(household) && !isNil(stagedHouseholdUuid) && (
            <Banner
              variant="destructive"
              title="Household not found"
              description={FIX_CONTACT_HOUSEHOLD_NOT_FOUND_DESCRIPTION}
            />
          )}

          {missingPrimaryClient && (
            <Banner
              variant="warning"
              title={FIX_CONTACT_MISSING_PRIMARY_TITLE}
              description={FIX_CONTACT_MISSING_PRIMARY_DESCRIPTION}
            />
          )}

          {!isLoading && isNil(loadError) && !isNil(household) && !isNil(primaryClient) && (
            <>
              {contactErrorCount > 0 && (
                <Banner
                  variant="warning"
                  title={
                    contactErrorCount === 1
                      ? '1 contact field needs attention'
                      : `${contactErrorCount} contact fields need attention`
                  }
                  description="Fix the fields marked Missing or invalid below before resolving."
                />
              )}

              <StagedClientContactFields
                client={primaryClient}
                savingClientId={mutations.savingClientId}
                onUpdateClientField={mutations.updateStagedClientField}
                onUpdateClientName={mutations.updateStagedClientName}
              />

              {coClients.map((client) => (
                <StagedClientContactFields
                  key={client.id}
                  client={client}
                  savingClientId={mutations.savingClientId}
                  onUpdateClientField={mutations.updateStagedClientField}
                  onUpdateClientName={mutations.updateStagedClientName}
                />
              ))}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            disabled={isResolving}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          {missingPrimaryClient ? (
            <Button onClick={handleOpenBulkUpload}>{FIX_CONTACT_OPEN_BULK_UPLOAD_LABEL}</Button>
          ) : (
            <Button
              disabled={resolveDisabled}
              isLoading={isResolving}
              aria-describedby={isSaving ? resolveStatusId : undefined}
              onClick={() => {
                void resolve();
              }}
            >
              Resolve
            </Button>
          )}
          <p id={resolveStatusId} className="sr-only" aria-live="polite">
            {isSaving ? 'Saving contact changes' : ''}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
